/**
 * AI camera operators.
 *
 * Each camera has an "operator" with human habits:
 *  - Pan/tilt/zoom are driven by critically damped springs (inertia, no overshoot),
 *    with a soft dead-zone so they don't chase every twitch of the ball.
 *  - They lead the play (aim ahead along the smoothed ball velocity) and give a
 *    moving player lead room.
 *  - They REACT to match events after a human reaction time (0.2–0.4 s, per operator,
 *    jittered per event). Cutting to a camera before its operator has re-framed is a
 *    real, visible thing — and the framing analysis reports it honestly.
 *  - On a big re-frame they whip-pan and zoom out slightly while searching, then
 *    settle back in.
 *  - Handhelds walk the touchline / goal line (speed-limited), with step bob and
 *    a little hand shake. Tripod/gantry cams are rock steady.
 *  - Director instructions (talkback) also arrive after a reaction delay.
 *
 * Everything here is deterministic given the same snapshots/events (no Math.random).
 */
import * as THREE from 'three';
import type {
  CameraDef, CameraFrame, CameraKind, MatchEvent, MatchEventType, OperatorInstruction,
  PlayerState, ShotSize, TeamSide, Vec3, WorldSnapshot,
} from '../../core/contracts';
import { PITCH, clamp, damp } from '../../core/math';
import { DEFAULT_CAMERA_ANCHORS } from './kit';
import {
  SHOT_SIZE_TARGET, analyzeFraming, anglesTo, fovForCoverage, fovForFraction, poseFromAngles, shiftSize,
  type CameraPose,
} from './framing';

export type FeedSource = 'live' | 'replay';

export interface CrewOptions {
  /** World anchors from the stadium module (merged over DEFAULT_CAMERA_ANCHORS). */
  anchors?: Record<string, Vec3>;
  /** Aspect used for framing analysis (monitors are 16:9). */
  aspect?: number;
  /** Varies operator personalities (reaction times, shake phases). */
  seed?: number;
}

export interface ReplayOptions {
  /** Seconds since the previous replay frame (defaults to |Δ snapshot.t|, capped). */
  dt?: number;
  /** The event the replay is about — operators frame its subject. */
  event?: MatchEvent | null;
}

export interface CameraCrew {
  readonly defs: readonly CameraDef[];
  /** Advance all operators. Returns one frame per camera (new objects each call). */
  update(snapshot: WorldSnapshot, dt: number, events: MatchEvent[]): CameraFrame[];
  setInstruction(camId: string, instruction: OperatorInstruction): void;
  getInstruction(camId: string): OperatorInstruction;
  getFrame(camId: string): CameraFrame | null;
  /** Latest live frames, kit order. */
  frames(): CameraFrame[];
  /** The Three camera for a feed. 'replay' cameras are posed by replayCamera(). */
  threeCamera(camId: string, source?: FeedSource): THREE.PerspectiveCamera;
  /**
   * Frame a historical snapshot for replays. Call once per replay frame per camera
   * shown. Operators snap to a good framing when the replay starts (or the
   * playhead jumps) and track with fast reflexes; SUPER SLO-MO goes tighter.
   */
  replayCamera(camId: string, snapshot: WorldSnapshot, opts?: ReplayOptions): CameraFrame;
  /** Reset replay operator state (call when a replay ends). */
  endReplay(): void;
  setAnchors(anchors: Record<string, Vec3>): void;
  /** Re-snap every live operator on the next update (e.g. after a scene jump). */
  reset(): void;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

interface Spring { x: number; v: number }

/**
 * Exact critically damped spring step (stable for any dt). `tv` is the target's
 * velocity (feed-forward): with tv = 0 a spring chasing a moving target lags by
 * 2·v/ω; operators anticipate most (not all) of that, like a human would.
 */
function springTo(s: Spring, target: number, omega: number, dt: number, tv = 0): void {
  const y = s.x - target;
  const rv = s.v - tv;
  const e = Math.exp(-omega * dt);
  const k = (rv + omega * y) * dt;
  s.x = target + tv * dt + (y + k) * e;
  s.v = tv + (rv - omega * k) * e;
}

/** How much of the target motion operators anticipate (1 = perfect tracking robot). */
const ANTICIPATION = 0.8;

const wrapPi = (a: number) => {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
};

function hash01(str: string, salt = 0): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

const v = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
const setV = (o: Vec3, x: number, y: number, z: number) => { o.x = x; o.y = y; o.z = z; return o; };
const copyV = (o: Vec3, a: Vec3) => setV(o, a.x, a.y, a.z);

// ---------------------------------------------------------------------------
// Perception — per-frame shared view of the world (one for live, one for replay)
// ---------------------------------------------------------------------------

interface Ctx {
  snap: WorldSnapshot;
  t: number;
  dt: number;
  byId: Map<string, PlayerState>;
  carrierId: string | null;
  /** Smoothed ball velocity (what an operator "feels" the play doing). */
  ballVel: Vec3;
  ballSpeed: number;
  /** +1 / -1: smoothed direction of play along x. */
  playDir: number;
  anchors: Record<string, Vec3>;
  crowdKeys: string[];
  replay: boolean;
}

class Perception {
  ctx: Ctx;
  private lastT = Number.NaN;
  private lastSnap: WorldSnapshot | null = null;
  private dirS = 1;

  constructor(anchors: Record<string, Vec3>, replay: boolean) {
    this.ctx = {
      snap: null as unknown as WorldSnapshot, t: 0, dt: 0, byId: new Map(), carrierId: null,
      ballVel: v(), ballSpeed: 0, playDir: 1, anchors, crowdKeys: [], replay,
    };
    this.setAnchors(anchors);
  }

  setAnchors(anchors: Record<string, Vec3>): void {
    this.ctx.anchors = anchors;
    this.ctx.crowdKeys = Object.keys(anchors).filter((k) => k.startsWith('crowd')).sort();
  }

  /** Force the next build to treat the snapshot as a discontinuity. */
  invalidate(): void { this.lastSnap = null; this.lastT = Number.NaN; }

  build(snap: WorldSnapshot, dt: number): { ctx: Ctx; jumped: boolean } {
    const c = this.ctx;
    if (snap === this.lastSnap && snap.t === this.lastT) return { ctx: c, jumped: false };
    const jumped = this.lastSnap === null || snap.t < this.lastT - 1e-6 || snap.t - this.lastT > 1.5;
    this.lastSnap = snap; this.lastT = snap.t;
    c.snap = snap; c.t = snap.t; c.dt = jumped ? 0 : Math.max(0, dt);
    c.byId.clear();
    let carrier: string | null = snap.ball.ownerId;
    for (const p of snap.players) {
      c.byId.set(p.id, p);
      if (!carrier && p.hasBall) carrier = p.id;
    }
    c.carrierId = carrier;
    const bv = snap.ball.vel;
    if (jumped) copyV(c.ballVel, bv);
    else {
      const k = damp(3.5, c.dt);
      c.ballVel.x += (bv.x - c.ballVel.x) * k;
      c.ballVel.y += (bv.y - c.ballVel.y) * k;
      c.ballVel.z += (bv.z - c.ballVel.z) * k;
    }
    c.ballSpeed = Math.hypot(c.ballVel.x, c.ballVel.z);
    const dirTarget = Math.abs(c.ballVel.x) > 1.5 ? Math.sign(c.ballVel.x) : this.dirS;
    this.dirS = jumped ? dirTarget : this.dirS + (dirTarget - this.dirS) * damp(1.2, c.dt);
    c.playDir = this.dirS >= 0 ? 1 : -1;
    return { ctx: c, jumped };
  }
}

// ---------------------------------------------------------------------------
// Operator
// ---------------------------------------------------------------------------

interface Mode {
  subject: string | 'ball' | null;
  /** Aim override (e.g. goalmouth, crowd section). */
  point?: Vec3;
  size?: ShotSize;
  coverage?: number;
  team?: TeamSide;
  until: number;
  prio: number;
}

interface Desire {
  aim: Vec3;
  subjectId: string | 'ball' | null;
  /** Point whose distance drives size→fov and framing composition. */
  subjectPoint: Vec3;
  size: ShotSize | null;
  coverage: number;
  dolly: Vec3 | null;
}

interface Personality { pan: number; zoom: number; dz: number; near: number; far: number }

const PERSONALITY: Record<CameraKind, Personality> = {
  main_wide: { pan: 2.4, zoom: 1.6, dz: 0.05, near: 1, far: 1500 },
  main_tight: { pan: 3.6, zoom: 2.2, dz: 0.03, near: 1, far: 1500 },
  high_18_left: { pan: 2.8, zoom: 1.8, dz: 0.04, near: 1, far: 1500 },
  high_18_right: { pan: 2.8, zoom: 1.8, dz: 0.04, near: 1, far: 1500 },
  behind_goal_left: { pan: 3.2, zoom: 2.0, dz: 0.04, near: 0.5, far: 1500 },
  behind_goal_right: { pan: 3.2, zoom: 2.0, dz: 0.04, near: 0.5, far: 1500 },
  pitchside_handheld_left: { pan: 4.2, zoom: 2.6, dz: 0.03, near: 0.1, far: 800 },
  pitchside_handheld_right: { pan: 4.2, zoom: 2.6, dz: 0.03, near: 0.1, far: 800 },
  reverse_angle: { pan: 2.4, zoom: 1.6, dz: 0.05, near: 1, far: 1500 },
  bench: { pan: 2.2, zoom: 1.5, dz: 0.05, near: 0.1, far: 800 },
  crowd: { pan: 1.3, zoom: 1.0, dz: 0, near: 0.5, far: 1500 },
  spidercam: { pan: 2.4, zoom: 1.6, dz: 0.04, near: 0.3, far: 1500 },
  blimp: { pan: 0.9, zoom: 0.6, dz: 0.03, near: 5, far: 4000 },
  super_slomo: { pan: 4.6, zoom: 2.6, dz: 0.025, near: 0.5, far: 1500 },
};

/** Size an operator uses when told "follow X". */
const NATURAL_FOLLOW_SIZE: Record<CameraKind, ShotSize> = {
  main_wide: 'wide', main_tight: 'medium', high_18_left: 'medium', high_18_right: 'medium',
  behind_goal_left: 'medium', behind_goal_right: 'medium',
  pitchside_handheld_left: 'close', pitchside_handheld_right: 'close',
  reverse_angle: 'wide', bench: 'medium', crowd: 'wide', spidercam: 'medium', blimp: 'extreme_wide', super_slomo: 'close',
};

/** Event types each operator family cares about (everything else is ignored cheaply). */
const SHOTS: MatchEventType[] = ['shot', 'shot_on_target', 'near_miss', 'woodwork'];
const CARE: Partial<Record<CameraKind, Set<MatchEventType>>> = {
  main_wide: new Set(['long_ball', 'cross', 'counter_attack', 'goal_kick', 'free_kick', 'corner', 'goal']),
  main_tight: new Set([...SHOTS, 'goal', 'yellow_card', 'red_card', 'injury', 'foul', 'substitution', 'save', 'celebration']),
  high_18_left: new Set([...SHOTS, 'save', 'goal', 'corner', 'free_kick', 'penalty']),
  behind_goal_left: new Set([...SHOTS, 'save', 'goal', 'corner', 'penalty']),
  pitchside_handheld_left: new Set(['goal', 'celebration', 'yellow_card', 'red_card', 'injury', 'foul', 'substitution', 'near_miss', 'woodwork', 'save']),
  bench: new Set(['manager_reaction', 'goal', 'substitution', 'yellow_card', 'red_card']),
  crowd: new Set(['goal', 'crowd_moment', 'near_miss', 'woodwork']),
  spidercam: new Set(['goal']),
};
CARE.high_18_right = CARE.high_18_left;
CARE.behind_goal_right = CARE.behind_goal_left;
CARE.pitchside_handheld_right = CARE.pitchside_handheld_left;
CARE.reverse_angle = CARE.main_wide;
CARE.super_slomo = CARE.main_tight;

/**
 * Handheld track: touchline then goal line, as a 2D polyline in (x, z). Starts beyond the
 * technical area (|x| ≲ 13.5, where managers stand at z ≈ -36.2) and runs a metre behind
 * the assistant referee's line (z = -35) so the lens isn't buried in a shoulder.
 */
const HANDHELD_Z = -37.6;
function handheldTrack(side: number): number[][] {
  return [[side * 14.5, HANDHELD_Z], [side * 54.5, HANDHELD_Z], [side * 54.5, -8.5]];
}

interface TrackPoint { x: number; z: number; s: number }

function nearestOnTrack(track: number[][], x: number, z: number, out: TrackPoint): TrackPoint {
  let best = Infinity, acc = 0;
  for (let i = 0; i < track.length - 1; i++) {
    const [ax, az] = track[i]!, [bx, bz] = track[i + 1]!;
    const dx = bx! - ax!, dz = bz! - az!, L = Math.hypot(dx, dz);
    const u = clamp(((x - ax!) * dx + (z - az!) * dz) / (L * L), 0, 1);
    const px = ax! + dx * u, pz = az! + dz * u;
    const d = Math.hypot(x - px, z - pz);
    if (d < best) { best = d; out.x = px; out.z = pz; out.s = acc + u * L; }
    acc += L;
  }
  return out;
}

function pointOnTrack(track: number[][], s: number, out: TrackPoint): TrackPoint {
  let acc = 0;
  for (let i = 0; i < track.length - 1; i++) {
    const [ax, az] = track[i]!, [bx, bz] = track[i + 1]!;
    const L = Math.hypot(bx! - ax!, bz! - az!);
    if (s <= acc + L || i === track.length - 2) {
      const u = clamp((s - acc) / L, 0, 1);
      out.x = ax! + (bx! - ax!) * u; out.z = az! + (bz! - az!) * u; out.s = s;
      return out;
    }
    acc += L;
  }
  return out;
}

function trackLength(track: number[][]): number {
  let L = 0;
  for (let i = 0; i < track.length - 1; i++) L += Math.hypot(track[i + 1]![0]! - track[i]![0]!, track[i + 1]![1]! - track[i]![1]!);
  return L;
}

class Operator {
  readonly def: CameraDef;
  readonly kind: CameraKind;
  readonly cam: THREE.PerspectiveCamera;
  readonly pers: Personality;
  /** -1 = x<0 end, +1 = x>0 end, 0 = central. */
  readonly side: number;
  readonly replay: boolean;
  instruction: OperatorInstruction = { kind: 'auto' };
  frame: CameraFrame;

  private reaction: number;
  private seedPhase: number;
  private pos: Vec3;
  private posV: Vec3 = v();
  private yaw: Spring = { x: 0, v: 0 };
  private pitch: Spring = { x: 0, v: 0 };
  private fov: Spring = { x: 30, v: 0 };
  private focal: Spring = { x: 50, v: 0 };
  private heldYaw = 0;
  private heldPitch = 0;
  /** Smoothed angular velocity of the held target (rad/s) — the operator's feel for the move. */
  private tvYaw = 0;
  private tvPitch = 0;
  private initialized = false;
  private pending: { at: number; ev: MatchEvent }[] = [];
  private pendingInstr: { at: number; instr: OperatorInstruction } | null = null;
  private mode: Mode | null = null;
  private perceivedCarrier: string | null = null;
  private carrierCandidate: string | null = null;
  private carrierSince = 0;
  private widen = 0;
  private track: number[][] | null = null;
  private trackLen = 0;
  private trackS: Spring = { x: 0, v: 0 };
  private dollyS = 0;
  private crowdIdx = 0;
  private crowdSince = 0;
  private benchTeam: TeamSide = 'home';
  private benchSince = 0;
  private lastSubjectKey = '';
  private whipUntil = -1;
  private now = 0;
  private sizeBias = 0;
  private readonly tp: TrackPoint = { x: 0, z: 0, s: 0 };
  private readonly desire: Desire = { aim: v(), subjectId: null, subjectPoint: v(), size: null, coverage: 30, dolly: null };
  private readonly dollyTarget: Vec3 = v();
  private readonly pose: CameraPose;
  private readonly aspect: number;
  private readonly tmpTarget = new THREE.Vector3();

  constructor(def: CameraDef, aspect: number, seed: number, replay: boolean) {
    this.def = def;
    this.kind = def.kind;
    this.replay = replay;
    this.aspect = aspect;
    this.pers = PERSONALITY[def.kind];
    this.side = /left/.test(def.kind) ? -1 : /right/.test(def.kind) ? 1 : 0;
    this.reaction = replay ? 0.05 : 0.2 + 0.2 * hash01(def.id, seed);
    this.seedPhase = hash01(def.id, seed + 7) * 100;
    this.pos = { ...def.position };
    this.cam = new THREE.PerspectiveCamera(def.fovRange[0], aspect, this.pers.near, this.pers.far);
    this.cam.name = `${def.id}${replay ? ' (replay)' : ''}`;
    if (def.kind === 'pitchside_handheld_left' || def.kind === 'pitchside_handheld_right') {
      this.track = handheldTrack(this.side);
      this.trackLen = trackLength(this.track);
      this.trackS.x = nearestOnTrack(this.track, def.position.x, def.position.z, this.tp).s;
    }
    if (replay && def.kind === 'super_slomo') this.sizeBias = 1;
    this.benchTeam = hash01(def.id, seed) > 0.5 ? 'home' : 'away';
    this.pose = poseFromAngles(this.pos, 0, 0, 0, def.fovRange[0], aspect, this.pers.near);
    this.frame = {
      camId: def.id, position: { ...def.position }, target: v(), fov: def.fovRange[0],
      framing: { subjectId: null, shotSize: 'wide', ballInFrame: false, focusInFrame: false, visible: [], quality: 0 },
    };
  }

  resnap(): void {
    this.initialized = false;
    this.pending.length = 0;
    this.mode = null;
    this.widen = 0;
  }

  setInstruction(instr: OperatorInstruction, now: number): void {
    // Talkback: the operator hears and acts after their reaction time.
    this.pendingInstr = { at: now + this.reaction, instr };
  }

  /** Immediately apply a reaction (used for replays). */
  reactNow(ev: MatchEvent, ctx: Ctx): void { this.react(ev, ctx); }

  // ---- main per-frame step ------------------------------------------------------
  step(ctx: Ctx, events: readonly MatchEvent[], jumped: boolean): CameraFrame {
    const t = ctx.t, dt = ctx.dt;
    this.now = t;
    if (jumped) this.initialized = false;

    // Hear events; act on them after reaction time.
    const care = CARE[this.kind];
    if (care) {
      for (const ev of events) {
        if (!care.has(ev.type)) continue;
        const jitter = (hash01(this.def.id, ev.id) - 0.5) * 0.1;
        this.pending.push({ at: Math.max(ev.t, t - dt) + Math.max(0.03, this.reaction + jitter), ev });
      }
    }
    if (this.pending.length) {
      for (let i = 0; i < this.pending.length; i++) {
        const p = this.pending[i]!;
        if (p.at <= t || !this.initialized) { this.react(p.ev, ctx); this.pending.splice(i--, 1); }
      }
    }
    if (this.pendingInstr && (this.pendingInstr.at <= t || !this.initialized)) {
      this.instruction = this.pendingInstr.instr;
      this.pendingInstr = null;
    }
    if (this.mode && (t > this.mode.until || (this.mode.subject && this.mode.subject !== 'ball' && !ctx.byId.has(this.mode.subject)))) this.mode = null;
    this.widen = Math.max(0, this.widen - dt / 3);

    // Perceived ball carrier (operators need a beat to notice possession changes).
    if (ctx.carrierId !== this.carrierCandidate) { this.carrierCandidate = ctx.carrierId; this.carrierSince = t; }
    if (!this.initialized || t - this.carrierSince >= this.reaction * 0.8) this.perceivedCarrier = this.carrierCandidate;
    if (this.perceivedCarrier && !ctx.byId.has(this.perceivedCarrier)) this.perceivedCarrier = null;

    // ---- decide what to shoot ----
    const d = this.think(ctx);
    if (this.instruction.kind === 'frame') d.size = this.instruction.size; // "go tight/wide", subject unchanged

    // ---- move the body (handheld / spidercam / blimp) ----
    this.moveBody(ctx, d);

    // ---- size → fov ----
    const [wideFov, tightFov] = this.def.fovRange;
    const sp = d.subjectPoint;
    const subjDist = Math.max(0.5, Math.hypot(sp.x - this.pos.x, sp.y - this.pos.y, sp.z - this.pos.z));
    const aimDist = Math.max(0.5, Math.hypot(d.aim.x - this.pos.x, d.aim.y - this.pos.y, d.aim.z - this.pos.z));
    if (!d.size && d.subjectId === 'ball' && this.kind !== 'main_wide' && this.kind !== 'blimp') {
      // Fast ball (pass, clearance, shot): loosen off so it can't escape the frame.
      const bv = ctx.snap.ball.vel;
      d.coverage += clamp(Math.hypot(bv.x, bv.z) - 12, 0, 25) * 0.6;
    }
    let fovT = d.size ? fovForFraction(SHOT_SIZE_TARGET[shiftSize(d.size, this.sizeBias)], subjDist) : fovForCoverage(d.coverage, aimDist, this.aspect);
    fovT = clamp(fovT, tightFov, wideFov);

    // ---- pan/tilt targets with dead-zone ----
    const raw = anglesTo(this.pos, d.aim);
    if (!this.initialized) {
      this.yaw.x = this.heldYaw = raw.yaw; this.pitch.x = this.heldPitch = raw.pitch;
      this.yaw.v = this.pitch.v = 0;
      this.fov.x = fovT; this.fov.v = 0;
      this.focal.x = aimDist; this.focal.v = 0;
      this.lastSubjectKey = this.subjectKey(d);
    }
    const vfov = (this.fov.x * Math.PI) / 180;
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * this.aspect);
    // Unwrap yaw near the current heading.
    const rawYaw = this.yaw.x + wrapPi(raw.yaw - this.yaw.x);
    const heldYaw0 = this.yaw.x + wrapPi(this.heldYaw - this.yaw.x);
    let ey = rawYaw - heldYaw0, ep = raw.pitch - this.heldPitch;
    const dzY = this.pers.dz * hfov, dzP = this.pers.dz * vfov;
    let hy = heldYaw0, hp = this.heldPitch;
    if (Math.abs(ey) > dzY) { hy += ey - Math.sign(ey) * dzY; ey = Math.sign(ey) * dzY; }
    if (Math.abs(ep) > dzP) { hp += ep - Math.sign(ep) * dzP; ep = Math.sign(ep) * dzP; }
    // Slow re-centring inside the dead-zone (operators tidy the frame when things calm down).
    const rc = damp(0.9, dt);
    hy += ey * rc; hp += ep * rc;
    // New subject → whip pan with faster reflexes and a search zoom-out.
    const key = this.subjectKey(d);
    const newSubject = key !== this.lastSubjectKey;
    if (newSubject) { this.lastSubjectKey = key; this.whipUntil = t + 0.9; }
    if (this.initialized && dt > 0 && !newSubject) {
      const k = damp(5, dt);
      this.tvYaw += (clamp((hy - heldYaw0) / dt, -2, 2) - this.tvYaw) * k;
      this.tvPitch += (clamp((hp - this.heldPitch) / dt, -1, 1) - this.tvPitch) * k;
    } else { this.tvYaw = 0; this.tvPitch = 0; }
    this.heldYaw = hy; this.heldPitch = hp;

    const whip = t < this.whipUntil;
    const omegaMul = (this.replay ? 2.2 : 1) * (whip ? 1.5 : 1);
    const angErr = Math.hypot((hy - this.yaw.x) * Math.cos(this.pitch.x), hp - this.pitch.x);
    if (angErr > 0.5 * vfov && this.initialized) {
      fovT = Math.min(wideFov, fovT * (1 + Math.min(angErr / vfov - 0.5, 2) * 0.35));
    }

    if (this.initialized && dt > 0) {
      springTo(this.yaw, hy, this.pers.pan * omegaMul, dt, this.tvYaw * ANTICIPATION);
      springTo(this.pitch, clamp(hp, -1.5, 1.2), this.pers.pan * omegaMul, dt, this.tvPitch * ANTICIPATION);
      springTo(this.fov, fovT, this.pers.zoom * (this.replay ? 2 : 1), dt);
      springTo(this.focal, aimDist, 3, dt);
      if (this.fov.x < tightFov) { this.fov.x = tightFov; this.fov.v = 0; }
      if (this.fov.x > wideFov) { this.fov.x = wideFov; this.fov.v = 0; }
    }
    this.initialized = true;

    // ---- handheld shake / step bob ----
    let sy = 0, sp2 = 0, roll = 0, bob = 0;
    if (this.track) {
      const walk = clamp(Math.abs(this.trackS.v) / 3, 0, 1);
      const ph = this.seedPhase, tt = t * Math.PI * 2;
      const amp = 0.0016 + 0.0045 * walk;
      sy = amp * (0.6 * Math.sin(tt * 0.63 + ph) + 0.3 * Math.sin(tt * 1.71 + ph * 1.3) + 0.1 * Math.sin(tt * 3.9 + ph * 2.1));
      sp2 = amp * (0.55 * Math.sin(tt * 0.51 + ph * 0.7) + 0.3 * Math.sin(tt * 2.23 + ph * 1.9)) + 0.004 * walk * Math.sin(tt * 1.9);
      roll = 0.0035 * Math.sin(tt * 0.37 + ph) + 0.0015 * Math.sin(tt * 1.13 + ph * 3);
      bob = 0.025 * walk * Math.sin(tt * 1.9 + ph);
    }

    const pose = poseFromAngles(
      { x: this.pos.x, y: this.pos.y + bob, z: this.pos.z }, this.yaw.x + sy, this.pitch.x + sp2, roll,
      this.fov.x, this.aspect, this.pers.near, this.pose,
    );
    const angSpeed = Math.hypot(this.yaw.v * Math.cos(this.pitch.x), this.pitch.v);
    const framing = analyzeFraming({ pose, snapshot: ctx.snap, subjectId: d.subjectId, subjectPoint: d.subjectPoint, angularSpeed: angSpeed });

    const f = pose.forward, fd = this.focal.x;
    const frame: CameraFrame = {
      camId: this.def.id,
      kind: this.def.kind,
      position: { x: pose.position.x, y: pose.position.y, z: pose.position.z },
      target: { x: pose.position.x + f.x * fd, y: pose.position.y + f.y * fd, z: pose.position.z + f.z * fd },
      fov: this.fov.x,
      framing,
    };
    this.frame = frame;

    // ---- pose the Three camera ----
    const cam = this.cam;
    cam.position.set(frame.position.x, frame.position.y, frame.position.z);
    cam.up.set(pose.up.x, pose.up.y, pose.up.z);
    cam.lookAt(this.tmpTarget.set(frame.target.x, frame.target.y, frame.target.z));
    if (cam.fov !== frame.fov) { cam.fov = frame.fov; cam.updateProjectionMatrix(); }
    return frame;
  }

  private subjectKey(d: Desire): string {
    return `${d.subjectId ?? ''}|${this.mode?.point ? 'p' : ''}|${this.instruction.kind}`;
  }

  // ---- event reactions ----------------------------------------------------------
  private setMode(m: Mode): void {
    if (!this.mode || this.now > this.mode.until || m.prio >= this.mode.prio) this.mode = m;
  }

  private keeperOf(ev: MatchEvent, ctx: Ctx): string | null {
    const ids = [ev.playerId, ev.secondaryPlayerId ?? null].filter((x): x is string => !!x && ctx.byId.has(x));
    let best: string | null = null, bd = Infinity;
    for (const id of ids) {
      const p = ctx.byId.get(id)!;
      const d = PITCH.halfL - Math.abs(p.pos.x);
      if (d < bd) { bd = d; best = id; }
    }
    return best;
  }

  private react(ev: MatchEvent, ctx: Ctx): void {
    const t = this.now;
    const side = this.side;
    const onMySide = side === 0 || ev.pos.x * side > -8;
    const pid = ev.playerId && ctx.byId.has(ev.playerId) ? ev.playerId : null;
    const goalX = side * PITCH.halfL;
    switch (this.kind) {
      case 'main_wide':
      case 'reverse_angle':
        if (ev.type === 'goal') this.setMode({ subject: pid ?? 'ball', coverage: 26, until: t + 8, prio: 2 });
        else this.widen = 1;
        break;
      case 'main_tight':
      case 'super_slomo': {
        const ssm = this.kind === 'super_slomo';
        switch (ev.type) {
          case 'goal': case 'celebration':
            if (pid) this.setMode({ subject: pid, size: ssm ? 'close' : 'medium', until: t + 10, prio: 3 }); break;
          case 'yellow_card': case 'red_card':
            if (pid) this.setMode({ subject: pid, size: ssm ? 'close' : 'medium', until: t + 6, prio: 3 }); break;
          case 'injury':
            if (pid) this.setMode({ subject: pid, size: 'close', until: t + 8, prio: 3 }); break;
          case 'foul': {
            const id = ev.secondaryPlayerId && ctx.byId.has(ev.secondaryPlayerId) ? ev.secondaryPlayerId : pid;
            if (id) this.setMode({ subject: id, size: 'medium', until: t + 3.5, prio: 2 }); break;
          }
          case 'substitution':
            if (pid) this.setMode({ subject: pid, size: 'medium', until: t + 6, prio: 2 }); break;
          case 'save': {
            const k = this.keeperOf(ev, ctx);
            if (k) this.setMode({ subject: k, size: 'medium', until: t + 3, prio: 2 }); break;
          }
          case 'near_miss': case 'woodwork':
            if (pid) this.setMode({ subject: pid, size: ssm ? 'close' : 'medium', until: t + 3, prio: 2 }); break;
          case 'shot': case 'shot_on_target':
            this.setMode({ subject: 'ball', size: ssm ? 'medium' : 'wide', until: t + 1.2, prio: 1 }); break;
          default: break;
        }
        break;
      }
      case 'high_18_left':
      case 'high_18_right':
        if (!onMySide) break;
        if (ev.type === 'goal') { if (pid) this.setMode({ subject: pid, size: 'medium', until: t + 9, prio: 3 }); }
        else if (ev.type === 'corner' || ev.type === 'free_kick' || ev.type === 'penalty') this.setMode({ subject: 'ball', point: v(side * 43, 0, 0), coverage: 34, until: t + 6, prio: 1 });
        else this.setMode({ subject: 'ball', point: v(goalX - side * 6, 0.8, clamp(ev.pos.z, -8, 8)), coverage: 22, until: t + 2.5, prio: 2 });
        break;
      case 'behind_goal_left':
      case 'behind_goal_right':
        if (ev.pos.x * side < 15) break;
        if (ev.type === 'goal') { if (pid) this.setMode({ subject: pid, size: 'medium', until: t + 9, prio: 3 }); }
        else if (ev.type === 'save') { const k = this.keeperOf(ev, ctx); if (k) this.setMode({ subject: k, size: 'medium', until: t + 3.5, prio: 3 }); }
        else if (ev.type === 'corner') this.setMode({ subject: 'ball', point: v(side * 47, 1, 0), coverage: 26, until: t + 7, prio: 1 });
        else if (ev.type === 'penalty') this.setMode({ subject: 'ball', point: v(side * 45, 1, 0), coverage: 20, until: t + 12, prio: 2 });
        else this.setMode({ subject: 'ball', point: v(goalX, 1.2, clamp(ev.pos.z, -4, 4)), coverage: 13, until: t + 3, prio: 2 });
        break;
      case 'pitchside_handheld_left':
      case 'pitchside_handheld_right': {
        const subjSide = pid ? ctx.byId.get(pid)!.pos.x * side > -10 : onMySide;
        switch (ev.type) {
          case 'goal':
            if (pid) this.setMode({ subject: pid, size: subjSide ? 'close' : 'medium', until: t + 14, prio: 3 }); break;
          case 'celebration': case 'yellow_card': case 'red_card':
            if (pid) this.setMode({ subject: pid, size: 'close', until: t + 6, prio: 3 }); break;
          case 'injury':
            if (pid) this.setMode({ subject: pid, size: 'close', until: t + 10, prio: 3 }); break;
          case 'substitution':
            if (pid) this.setMode({ subject: pid, size: 'medium', until: t + 6, prio: 2 }); break;
          case 'foul': {
            if (!onMySide) break;
            const id = ev.secondaryPlayerId && ctx.byId.has(ev.secondaryPlayerId) ? ev.secondaryPlayerId : pid;
            if (id) this.setMode({ subject: id, size: 'medium', until: t + 4, prio: 2 }); break;
          }
          case 'near_miss': case 'woodwork':
            if (onMySide && pid) this.setMode({ subject: pid, size: 'close', until: t + 3.5, prio: 2 }); break;
          case 'save': {
            const k = this.keeperOf(ev, ctx);
            if (onMySide && k) this.setMode({ subject: k, size: 'medium', until: t + 3, prio: 2 }); break;
          }
          default: break;
        }
        break;
      }
      case 'bench':
        if (ev.team) this.setMode({ subject: null, team: ev.team, size: ev.type === 'manager_reaction' ? 'close' : 'medium', until: t + (ev.type === 'goal' ? 8 : 6), prio: 2 });
        break;
      case 'crowd': {
        const a = ctx.anchors;
        let key: string | undefined;
        if (ev.type === 'goal' && ev.team) key = a[`crowd_${ev.team}_end`] ? `crowd_${ev.team}_end` : undefined;
        if (!key) {
          let bd = Infinity;
          for (const k of ctx.crowdKeys) { const p = a[k]!; const d = Math.hypot(p.x - ev.pos.x, p.z - ev.pos.z); if (d < bd) { bd = d; key = k; } }
        }
        if (key) this.setMode({ subject: null, point: a[key]!, until: t + (ev.type === 'goal' ? 10 : 5), prio: 2 });
        break;
      }
      case 'spidercam':
        if (pid) this.setMode({ subject: pid, size: 'medium', until: t + 8, prio: 3 });
        break;
      case 'blimp':
        break;
    }
  }

  // ---- shot selection -------------------------------------------------------------
  private subjectPos(ctx: Ctx, id: string | 'ball' | null): Vec3 | null {
    if (id === 'ball') return ctx.snap.ball.pos;
    if (!id) return null;
    return ctx.byId.get(id)?.pos ?? null;
  }

  private nearestTo(ctx: Ctx, p: Vec3): string | null {
    let best: string | null = null, bd = Infinity;
    for (const pl of ctx.snap.players) {
      const d = (pl.pos.x - p.x) ** 2 + (pl.pos.z - p.z) ** 2;
      if (d < bd) { bd = d; best = pl.id; }
    }
    return best;
  }

  /** Frame a person/ball at a shot size, with lead room for movement. */
  private frameSubject(ctx: Ctx, id: string | 'ball', size: ShotSize, d: Desire): void {
    const sp = this.subjectPos(ctx, id) ?? ctx.snap.ball.pos;
    d.subjectId = id;
    d.size = size;
    copyV(d.subjectPoint, sp);
    const h = size === 'extreme_wide' ? 0 : size === 'wide' ? 0.5 : size === 'medium' ? 1.0 : size === 'close' ? 1.3 : 1.55;
    let lx = 0, lz = 0;
    if (id === 'ball') {
      const k = size === 'wide' || size === 'extreme_wide' ? 0.45 : 0.25;
      lx = clamp(ctx.ballVel.x * k, -6, 6); lz = clamp(ctx.ballVel.z * k, -4, 4);
      setV(d.aim, sp.x + lx, sp.y * 0.6 + Math.min(h, 1), sp.z + lz);
    } else {
      const pl = ctx.byId.get(id);
      if (pl) {
        const spd = Math.hypot(pl.vel.x, pl.vel.z);
        if (spd > 0.5) {
          const frameW = (1.8 / SHOT_SIZE_TARGET[size]) * this.aspect;
          const k = Math.min(spd / 6, 1) * 0.15 * frameW / spd;
          lx = pl.vel.x * k; lz = pl.vel.z * k;
        }
      }
      setV(d.aim, sp.x + lx, sp.y + h, sp.z + lz);
    }
  }

  private frameCoverage(point: Vec3, width: number, d: Desire, subject: string | 'ball' | null, ctx: Ctx): void {
    copyV(d.aim, point);
    d.size = null;
    d.coverage = width;
    d.subjectId = subject;
    copyV(d.subjectPoint, subject === 'ball' ? ctx.snap.ball.pos : point);
  }

  private think(ctx: Ctx): Desire {
    const d = this.desire;
    d.dolly = null;
    const s = ctx.snap, ball = s.ball.pos, t = ctx.t;
    const instr = this.instruction;
    const m = this.mode;

    // ---- director instructions win ----
    if (instr.kind === 'follow_player' && ctx.byId.has(instr.playerId)) {
      this.frameSubject(ctx, instr.playerId, NATURAL_FOLLOW_SIZE[this.kind], d);
      this.dollyToward(d.subjectPoint, d, ctx);
      return d;
    }
    if (instr.kind === 'follow_ball') {
      this.frameSubject(ctx, 'ball', NATURAL_FOLLOW_SIZE[this.kind], d);
      this.dollyToward(d.subjectPoint, d, ctx);
      return d;
    }
    if (instr.kind === 'crowd') { this.crowdShot(ctx, d); return d; }
    if (instr.kind === 'bench') { this.benchShot(ctx, d, instr.team, null); return d; }

    switch (this.kind) {
      case 'main_wide':
      case 'reverse_angle': {
        const rev = this.kind === 'reverse_angle';
        if (m && m.subject && m.subject !== 'ball') {
          const p = this.subjectPos(ctx, m.subject)!;
          this.frameCoverage(v(p.x, 0.6, p.z), m.coverage ?? 26, d, m.subject, ctx);
        } else if (s.phase === 'kickoff' || s.phase === 'pre_match' || s.phase === 'half_time' || s.phase === 'full_time') {
          this.frameCoverage(v(ball.x * 0.5, 0, ball.z * 0.3), rev ? 48 : 55, d, 'ball', ctx);
        } else if (s.phase === 'goal_celebration') {
          this.frameCoverage(v(s.focus.x, 0.5, s.focus.z), 30, d, this.nearestTo(ctx, s.focus) ?? 'ball', ctx);
        } else {
          const lead = 0.7;
          const lx = clamp(ctx.ballVel.x * lead, -12, 12), lz = clamp(ctx.ballVel.z * lead * 0.6, -6, 6);
          const x = clamp(ball.x + lx, -44, 44);
          const z = ball.z * 0.75 + lz + (rev ? -2 : 2);
          let cov = (rev ? 30 : 34) + clamp(ctx.ballSpeed - 14, 0, 18) * 0.8 + 18 * this.widen;
          if (s.phase === 'set_piece' || s.phase === 'stoppage') cov = Math.max(cov, 38);
          cov -= 4 * s.intensity * (Math.abs(ball.x) > 30 ? 1 : 0);
          this.frameCoverage(v(x, ball.y * 0.4, z), cov, d, 'ball', ctx);
        }
        break;
      }
      case 'main_tight':
      case 'super_slomo': {
        const ssm = this.kind === 'super_slomo';
        if (m && m.subject) this.frameSubject(ctx, m.subject, m.size ?? 'medium', d);
        else if (s.phase === 'goal_celebration') this.frameSubject(ctx, this.nearestTo(ctx, s.focus) ?? 'ball', ssm ? 'close' : 'medium', d);
        else if (this.perceivedCarrier) this.frameSubject(ctx, this.perceivedCarrier, 'medium', d); // SSM goes close in replays (sizeBias)
        else this.frameSubject(ctx, 'ball', ctx.ballSpeed > 12 || ball.y > 2 ? 'wide' : 'medium', d);
        break;
      }
      case 'high_18_left':
      case 'high_18_right': {
        const side = this.side;
        if (m && m.subject && m.subject !== 'ball') this.frameSubject(ctx, m.subject, m.size ?? 'medium', d);
        else if (m && m.point) this.frameCoverage(m.point, m.coverage ?? 26, d, 'ball', ctx);
        else if (ball.x * side > -5) {
          const lx = clamp(ctx.ballVel.x * 0.5, -8, 8), lz = clamp(ctx.ballVel.z * 0.35, -5, 5);
          const inBox = Math.abs(ball.x) > PITCH.halfL - PITCH.boxDepth;
          const cov = inBox ? 21 : Math.max(19, 27 - 6 * s.intensity);
          this.frameCoverage(v(ball.x + lx, ball.y * 0.4, ball.z + lz), cov, d, 'ball', ctx);
        } else {
          // Other half: hold a wide on our end's shape, drifting with the ball.
          const ax = ball.x + (side * 30 - ball.x) * 0.4;
          this.frameCoverage(v(ax, 0, ball.z * 0.6), 44, d, 'ball', ctx);
        }
        break;
      }
      case 'behind_goal_left':
      case 'behind_goal_right': {
        const goalX = this.side * PITCH.halfL;
        if (m && m.subject && m.subject !== 'ball') this.frameSubject(ctx, m.subject, m.size ?? 'medium', d);
        else if (m && m.point) this.frameCoverage(m.point, m.coverage ?? 14, d, 'ball', ctx);
        else {
          const bd = Math.abs(ball.x - goalX);
          if (bd < 32) {
            const lx = clamp(ctx.ballVel.x * 0.4, -5, 5), lz = clamp(ctx.ballVel.z * 0.4, -5, 5);
            this.frameCoverage(v(ball.x + lx, ball.y * 0.5 + 0.3, ball.z + lz), 13 + bd * 0.55, d, 'ball', ctx);
          } else {
            this.frameCoverage(v(ball.x, 0, ball.z * 0.8), 40, d, 'ball', ctx);
          }
        }
        break;
      }
      case 'pitchside_handheld_left':
      case 'pitchside_handheld_right': {
        if (m && m.subject) this.frameSubject(ctx, m.subject, m.size ?? 'close', d);
        else if (s.phase === 'goal_celebration') this.frameSubject(ctx, this.nearestTo(ctx, s.focus) ?? 'ball', 'close', d);
        else {
          const subj = this.perceivedCarrier ?? 'ball';
          const sp = this.subjectPos(ctx, subj)!;
          if (sp.x * this.side < -12) {
            // Play is on the other half: hold a low wide looking across the action.
            this.frameSubject(ctx, 'ball', 'wide', d);
          } else {
            const dist = Math.hypot(sp.x - this.pos.x, sp.z - this.pos.z);
            this.frameSubject(ctx, subj, dist < 24 ? 'medium' : 'wide', d);
          }
        }
        this.dollyToward(d.subjectPoint, d, ctx);
        break;
      }
      case 'bench':
        this.benchShot(ctx, d, m?.team ?? null, m?.size ?? null);
        break;
      case 'crowd':
        this.crowdShot(ctx, d);
        break;
      case 'spidercam': {
        if (m && m.subject) this.frameSubject(ctx, m.subject, m.size ?? 'medium', d);
        else this.frameCoverage(v(ball.x + clamp(ctx.ballVel.x * 0.5, -6, 6), ball.y * 0.5, ball.z + clamp(ctx.ballVel.z * 0.4, -4, 4)), 24, d, 'ball', ctx);
        const bx = d.subjectPoint.x, bz = d.subjectPoint.z;
        d.dolly = setV(this.dollyTarget, clamp(bx - ctx.playDir * 13, -48, 48), 15 + 2 * Math.sin(t * 0.2), clamp(bz - 9, -31, 31));
        break;
      }
      case 'blimp': {
        const th = 0.55 * Math.sin(t * 0.012 + this.seedPhase);
        d.dolly = setV(this.dollyTarget, 190 * Math.sin(th), 160, -190 * Math.cos(th));
        this.frameCoverage(v(ball.x * 0.35, 0, ball.z * 0.35), 150, d, 'ball', ctx);
        break;
      }
    }
    return d;
  }

  private benchShot(ctx: Ctx, d: Desire, team: TeamSide | null, size: ShotSize | null): void {
    if (!team) {
      // Idle bench op alternates between the two technical areas every ~20 s.
      if (ctx.t - this.benchSince > 20) { this.benchSince = ctx.t; this.benchTeam = this.benchTeam === 'home' ? 'away' : 'home'; }
      team = this.benchTeam;
    } else { this.benchTeam = team; this.benchSince = ctx.t; }
    const a = ctx.anchors[`dugout_${team}`] ?? DEFAULT_CAMERA_ANCHORS[`dugout_${team}`]!;
    // Small searching drift — a person framing a person.
    const tt = ctx.t + this.seedPhase;
    const p = setV(d.subjectPoint, a.x + 0.5 * Math.sin(tt * 0.21), a.y, a.z);
    setV(d.aim, p.x, p.y + 0.35 + 0.08 * Math.sin(tt * 0.33), p.z);
    d.subjectId = null;
    d.size = size ?? 'medium';
  }

  private crowdShot(ctx: Ctx, d: Desire): void {
    const m = this.mode;
    let a: Vec3 | undefined = m?.point;
    let since = this.crowdSince;
    if (!a) {
      const keys = ctx.crowdKeys;
      if (ctx.t - this.crowdSince > 8) { this.crowdSince = ctx.t; this.crowdIdx = (this.crowdIdx + 1) % Math.max(1, keys.length); }
      since = this.crowdSince;
      a = keys.length ? ctx.anchors[keys[this.crowdIdx % keys.length]!]! : DEFAULT_CAMERA_ANCHORS.crowd_far!;
    }
    // Slow lateral pan across the section (perpendicular to the view line).
    const dx = a.x - this.pos.x, dz = a.z - this.pos.z, L = Math.hypot(dx, dz) || 1;
    const sweep = 11 * Math.sin((ctx.t - since) * 0.22 + this.seedPhase);
    setV(d.aim, a.x - (dz / L) * sweep, a.y, a.z + (dx / L) * sweep);
    copyV(d.subjectPoint, d.aim);
    d.subjectId = null;
    d.size = null;
    d.coverage = m ? 16 : 24;
  }

  private dollyToward(p: Vec3, d: Desire, ctx: Ctx): void {
    if (this.track) {
      nearestOnTrack(this.track, p.x, p.z, this.tp);
      d.dolly = setV(this.dollyTarget, this.tp.x, 1.6, this.tp.z);
      this.dollyS = clamp(this.tp.s, 0, this.trackLen);
    } else if (this.kind === 'spidercam') {
      d.dolly = setV(this.dollyTarget, clamp(p.x - ctx.playDir * 10, -48, 48), 12, clamp(p.z - 8, -31, 31));
    }
  }

  private moveBody(ctx: Ctx, d: Desire): void {
    const dt = ctx.dt;
    if (this.track) {
      let sTarget = d.dolly ? this.dollyS : this.trackS.x;
      // Side-step anyone standing right in front of the lens (assistant referee running the
      // line, a player taking a throw): keep ≥3 m of track between us and them.
      if (this.trackS.x < 39) { // touchline segment (40 m) only
        const o = ctx.snap.officials;
        for (const f of [o.assistant1, o.assistant2, ...ctx.snap.players.map((pl) => pl.pos)]) {
          if (Math.abs(f.z - this.pos.z) > 3.2) continue;
          const dx = (this.pos.x - f.x) * this.side; // + = we're further from halfway than them
          if (Math.abs(dx) < 3) sTarget = this.trackS.x + (dx >= 0 ? 3 - dx : -(3 + dx));
        }
        sTarget = clamp(sTarget, 0, this.trackLen);
      }
      if (!this.initialized || dt <= 0) { if (!this.initialized) { this.trackS.x = sTarget; this.trackS.v = 0; } }
      else {
        springTo(this.trackS, sTarget, this.replay ? 3 : 1.3, dt);
        const vmax = 6.5; // jogging with a steadicam rig
        if (Math.abs(this.trackS.v) > vmax) { this.trackS.v = Math.sign(this.trackS.v) * vmax; }
        this.trackS.x = clamp(this.trackS.x, 0, this.trackLen);
      }
      pointOnTrack(this.track, this.trackS.x, this.tp);
      setV(this.pos, this.tp.x, 1.6, this.tp.z);
      return;
    }
    if (!d.dolly) return;
    if (!this.initialized || dt <= 0) { if (!this.initialized) { copyV(this.pos, d.dolly); setV(this.posV, 0, 0, 0); } return; }
    if (this.kind === 'blimp') { copyV(this.pos, d.dolly); return; }
    const w = this.replay ? 2.5 : 0.9;
    const sx = { x: this.pos.x, v: this.posV.x }, sy = { x: this.pos.y, v: this.posV.y }, sz = { x: this.pos.z, v: this.posV.z };
    springTo(sx, d.dolly.x, w, dt); springTo(sy, d.dolly.y, w, dt); springTo(sz, d.dolly.z, w, dt);
    const sp = Math.hypot(sx.v, sy.v, sz.v), vmax = 9;
    const k = sp > vmax ? vmax / sp : 1;
    setV(this.pos, sx.x, sy.x, sz.x); setV(this.posV, sx.v * k, sy.v * k, sz.v * k);
  }
}

// ---------------------------------------------------------------------------
// Crew
// ---------------------------------------------------------------------------

export function createCameraCrew(kit: readonly CameraDef[], opts: CrewOptions = {}): CameraCrew {
  const aspect = opts.aspect ?? 16 / 9;
  const seed = opts.seed ?? 1;
  let anchors: Record<string, Vec3> = { ...DEFAULT_CAMERA_ANCHORS, ...(opts.anchors ?? {}) };
  const live = new Map<string, Operator>();
  const replayOps = new Map<string, Operator>();
  for (const def of kit) live.set(def.id, new Operator(def, aspect, seed, false));
  const livePerception = new Perception(anchors, false);
  const replayPerception = new Perception(anchors, true);
  const replayReacted = new Map<string, number>(); // camId -> event id already applied
  let lastT = 0;
  let replayLastT = Number.NaN;

  const getOp = (camId: string): Operator => {
    const op = live.get(camId);
    if (!op) throw new Error(`Unknown camera ${camId}`);
    return op;
  };
  const getReplayOp = (camId: string): Operator => {
    let op = replayOps.get(camId);
    if (!op) {
      op = new Operator(getOp(camId).def, aspect, seed, true);
      replayOps.set(camId, op);
    }
    return op;
  };

  return {
    defs: kit,
    update(snapshot, dt, events) {
      const { ctx, jumped } = livePerception.build(snapshot, dt);
      lastT = snapshot.t;
      const out: CameraFrame[] = [];
      for (const op of live.values()) out.push(op.step(ctx, events, jumped));
      return out;
    },
    setInstruction(camId, instruction) { getOp(camId).setInstruction(instruction, lastT); },
    getInstruction(camId) { return getOp(camId).instruction; },
    getFrame(camId) { return live.get(camId)?.frame ?? null; },
    frames() { return [...live.values()].map((o) => o.frame); },
    threeCamera(camId, source = 'live') { return source === 'replay' ? getReplayOp(camId).cam : getOp(camId).cam; },
    replayCamera(camId, snapshot, ro = {}) {
      const op = getReplayOp(camId);
      const dtGuess = Number.isNaN(replayLastT) ? 0 : Math.min(0.1, Math.abs(snapshot.t - replayLastT));
      const { ctx, jumped } = replayPerception.build(snapshot, ro.dt ?? dtGuess);
      replayLastT = snapshot.t;
      if (jumped) { for (const o of replayOps.values()) o.resnap(); replayReacted.clear(); }
      const ev = ro.event ?? null;
      if (ev && replayReacted.get(camId) !== ev.id && snapshot.t >= ev.t) {
        replayReacted.set(camId, ev.id);
        op.reactNow(ev, ctx);
      }
      // The shared replay perception caches per snapshot, so every replay camera shown this
      // frame integrates the same dt.
      return op.step(ctx, [], jumped);
    },
    endReplay() {
      for (const o of replayOps.values()) o.resnap();
      replayReacted.clear();
      replayPerception.invalidate();
      replayLastT = Number.NaN;
    },
    setAnchors(a) {
      anchors = { ...DEFAULT_CAMERA_ANCHORS, ...a };
      livePerception.setAnchors(anchors);
      replayPerception.setAnchors(anchors);
    },
    reset() { for (const o of live.values()) o.resnap(); livePerception.invalidate(); },
  };
}
